import React from 'react';
import PropTypes from 'prop-types';
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import {Chip, Grid, Typography} from "@mui/material";

function BranchAdminInfo({branchAdmin}) {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE':
                return 'success';
            case 'INACTIVE':
                return 'error';
            case 'PENDING':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getGenderDisplayName = (gender) => {
        switch (gender) {
            case 'MALE':
                return 'Male';
            case 'FEMALE':
                return 'Female';
            case 'OTHER':
                return 'Other';
            default:
                return 'N/A';
        }
    };

    const getIdTypeDisplayName = (idType) => {
        switch (idType) {
            case 'IC':
                return 'IC';
            case 'PASSPORT':
                return 'Passport';
            case 'DRIVING_LICENSE':
                return 'Driving License';
            default:
                return 'N/A';
        }
    };

    // The API returns the branch flattened onto the admin (branchName/branchId),
    // not as a nested object — reading only `admin.branch` made this render
    // "N/A" for every branch admin. Nested shapes are still accepted in case
    // another endpoint returns one.
    const getBranchDisplayName = (admin) => {
        if (!admin) return 'N/A';
        return admin.branchName
            || admin.branch?.branchName
            || admin.branch?.name
            || 'N/A';
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Profile Information
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Email Address
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {branchAdmin.email || 'N/A'}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Phone Number
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {branchAdmin.phoneNumber || 'N/A'}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Gender
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {getGenderDisplayName(branchAdmin.gender)}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Birth Date
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {formatDate(branchAdmin.birthDate)}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                ID Type
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {getIdTypeDisplayName(branchAdmin.idType)}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                ID Number
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {branchAdmin.idNumber || 'N/A'}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Branch
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {getBranchDisplayName(branchAdmin)}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                User Role
                            </SoftTypography>
                            <Chip
                                label={branchAdmin.userRole || 'BRANCH_ADMIN'}
                                color="primary"
                                size="small"
                                variant="outlined"
                            />
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Status
                            </SoftTypography>
                            <Chip
                                label={branchAdmin.status || 'ACTIVE'}
                                color={getStatusColor(branchAdmin.status)}
                                size="small"
                                variant="outlined"
                            />
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Last Login
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {formatDateTime(branchAdmin.lastLogin)}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Username
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {branchAdmin.username || 'N/A'}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Created At
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {formatDateTime(branchAdmin.createdAt)}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="bold" color="text">
                                Updated At
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text">
                                {formatDateTime(branchAdmin.updatedAt)}
                            </SoftTypography>
                        </SoftBox>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

BranchAdminInfo.propTypes = {
    branchAdmin: PropTypes.shape({
        email: PropTypes.string,
        phoneNumber: PropTypes.string,
        gender: PropTypes.string,
        birthDate: PropTypes.string,
        idType: PropTypes.string,
        idNumber: PropTypes.string,
        branch: PropTypes.object,
        userRole: PropTypes.string,
        status: PropTypes.string,
        lastLogin: PropTypes.string,
        username: PropTypes.string,
        createdAt: PropTypes.string,
        updatedAt: PropTypes.string,
    }).isRequired,
};

export default BranchAdminInfo;
